import {
  CheckboxInput,
  ConfigurationContext,
  DataTableExRef,
  DateInput,
  FileInput,
  FormPageLayout,
  Grid,
  LanguagesContext,
  TranslationInput,
} from '@kirz/mui-admin';
import { Add } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { DatabaseOutline } from 'mdi-material-ui';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AudiosTable } from 'components/AudiosTable';

export function Category() {
  const routeParams = useParams() as Record<string, string>;
  const categoryId = parseInt(routeParams.id, 10);
  const tableRef = useRef<DataTableExRef>(null);
  const { hasura } = useContext(ConfigurationContext);
  const { defaultLanguageId } = useContext(LanguagesContext);
  const [sortValues, setSortValues] = useState<number[] | null>(null);

  useEffect(() => {
    (async () => {
      const audios = await hasura.request({
        type: 'query',
        source: 'audio',
        selection: 'serialNumber',
        where: {
          categoryId: { _eq: categoryId },
        },
      });

      setSortValues(audios.map((x: any) => x.serialNumber));
    })();
  }, [categoryId]);

  return (
    <FormPageLayout
      source="category"
      breadcrumbs={[
        {
          text: 'Categories',
          icon: DatabaseOutline,
          href: '/categories',
        },
        (item) => ({
          text: `#${item.id} ${item.name?.[defaultLanguageId]}`,
          copyOnClick: true,
        }),
      ]}
      breadcrumbsDeps={['name']}
      defaultRoute="/categories"
    >
      <Grid container alignItems="flex-start">
        <TranslationInput name="name" label="Name" required />
        <FileInput
          name="imageId"
          label="Image"
          required
          sm={6}
          md={3}
          dropzoneProps={{
            accept: {
              'image/png': ['.png'],
              'image/jpeg': ['.jpg', '.jpeg'],
            },
          }}
        />
        <FileInput
          name="bigImageId"
          label="Big image"
          sm={6}
          md={3}
          dropzoneProps={{
            accept: {
              'image/png': ['.png'],
              'image/jpeg': ['.jpg', '.jpeg'],
            },
          }}
        />
        <DateInput name="newUntil" label="New until" sm={3} md={2} />
        <DateInput name="visibleFrom" label="Visible from" sm={3} md={2} />
        <CheckboxInput name="isActive" label="Active" sm="auto" />
      </Grid>
      <Box
        sx={{
          mt: 3,
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}
      >
        <AudiosTable
          id="category-audios-table"
          ref={tableRef}
          searchFilter={
            {
              slots: {
                beforeSearch: (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle1">Meditations</Typography>
                    <Button
                      startIcon={<Add />}
                      variant="outlined"
                      size="small"
                      sx={{ mx: 2, whiteSpace: 'pre' }}
                      onClick={() => {
                        tableRef.current?.openFormDialog();
                      }}
                    >
                      Add new
                    </Button>
                  </Box>
                ),
              },
            } as any
          }
          deletable={{
            onDeleted: (row) => {
              setSortValues(sortValues!.filter((x) => x !== row.serialNumber));
            },
          }}
          sortBy={{ field: 'serialNumber', sort: 'asc' }}
          sx={{
            mt: 1,
            minHeight: 400,
            mx: -2,
            borderColor: 'transparent',
          }}
          selectProps={{
            filter: {
              categoryId: { _eq: categoryId },
            },
          }}
          formDialogProps={{
            formProps: {
              defaultValues: {
                categoryId,
              },
            },
          }}
          filterColumns={(columns) => [
            {
              field: 'serialNumber',
              type: 'sort',
              sortKey: 'serialNumber',
              rowsSortValues: sortValues,
              onSortChange: async (direction, row, targetRowSort) => {
                const source = 'Audio';
                const { id } = row;

                await hasura.request({
                  type: 'custom',
                  query: `
                    mutation ChangeSort($where1: ${source}BoolExp!, $set1: ${source}SetInput!, $where2: ${source}BoolExp!, $set2: ${source}SetInput!) {
                      result2: update${source}(where: $where2, _set: $set2) {
                        affected_rows
                      }
                      result1: update${source}(where: $where1, _set: $set1) {
                        affected_rows
                      }
                    }
                  `,
                  variables: {
                    where1: { id: { _eq: id } },
                    set1: { serialNumber: targetRowSort },
                    where2: {
                      serialNumber: { _eq: targetRowSort },
                      categoryId: { _eq: categoryId },
                    },
                    set2: { serialNumber: row.serialNumber },
                  },
                });

                tableRef.current?.reload();
              },
            },
            ...columns
              .filter((x) => !['Category'].includes(x.headerName!))
              .map((x) => ({ ...x, sortable: false })),
          ]}
        />
      </Box>
    </FormPageLayout>
  );
}
